/**
 * ModelProviderTransparencyPage (DIGEMP-MODEL-PROVIDER-TRANSPARENCY-FRONTEND-064) — read-only
 * operator surface explaining how each Digital Employee's AI is routed: the assigned model and
 * where it comes from, the reported fallback policy, the provider/model actually observed on real
 * runs, and the available health/cost evidence. Credential-shaped values are refused and nothing
 * here edits routing policy or needs anything beyond the agents_view read gate.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { agentRunsApi, agentsApi } from "../services/api";
import RunCostChips from "../components/RunCostChips";

// ─── Safe-value rendering ─────────────────────────────────────────────────────
// mt* prefix keeps these helpers distinct from sibling workspace panels (029/041/042 use their
// own prefixes) so parallel deliveries merge without name collisions.

export const MT_SECRET_REFUSED = "לא מוצג — הערך בצורת סוד";
const MT_SECRET_PREFIXES = ["sk-", "AKIA", "AIza", "ghp_", "xox", "hvs.", "eyJ", "Bearer ", "-----BEGIN"];
const MT_BASE64_RE = /^[A-Za-z0-9+/]{40,}={0,2}$/;
const MT_HEX_RE = /^[0-9a-fA-F]{32,}$/;
const MT_ALNUM_RE = /^[A-Za-z0-9]{40,}$/;

export function mtCredentialShaped(value) {
  const text = String(value ?? "").trim();
  if (!text) return false;
  if (MT_SECRET_PREFIXES.some((p) => text.startsWith(p))) return true;
  return MT_BASE64_RE.test(text) || MT_HEX_RE.test(text) || MT_ALNUM_RE.test(text);
}

export function mtSafeText(value, fallback = "—") {
  if (value == null || value === "") return fallback;
  const text = String(value);
  return mtCredentialShaped(text) ? MT_SECRET_REFUSED : text;
}

// ─── Labels ───────────────────────────────────────────────────────────────────

const MT_PROVIDER_LABELS = {
  anthropic: "Anthropic (Claude)",
  openai: "OpenAI",
  openclaw: "OpenClaw",
  azure: "Azure OpenAI",
  "azure-openai": "Azure OpenAI",
  google: "Google",
  gemini: "Google Gemini",
  ollama: "Ollama",
  local: "מודל מקומי",
};

export function mtProviderLabel(provider) {
  if (provider == null || provider === "") return null;
  const text = mtSafeText(provider);
  if (text === MT_SECRET_REFUSED) return text;
  return MT_PROVIDER_LABELS[String(provider).toLowerCase()] ?? text;
}

const MT_SOURCE_LABELS = {
  agent_record: "לפי רשומת העובד",
  record: "לפי רשומת העובד",
  employee_record: "לפי רשומת העובד",
  policy: "לפי מדיניות ניתוב",
  routing_policy: "לפי מדיניות ניתוב",
  model_policy: "לפי מדיניות המודל בשרת",
  default: "ברירת מחדל של השרת",
  server_default: "ברירת מחדל של השרת",
  override: "עקיפת עדיפות גבוהה",
  high_priority_override: "עקיפת עדיפות גבוהה",
  fallback: "מסלול גיבוי",
};

export function mtSourceLabel(source) {
  if (source == null || source === "") return null;
  const text = mtSafeText(source);
  if (text === MT_SECRET_REFUSED) return text;
  return MT_SOURCE_LABELS[String(source).toLowerCase()] ?? text;
}

const MT_HEALTH_META = {
  healthy:     { label: "בריא",     cls: "bg-green-100 text-green-700" },
  degraded:    { label: "פגום",     cls: "bg-amber-100 text-amber-700" },
  unreachable: { label: "לא נגיש",  cls: "bg-red-100 text-red-700" },
  unknown:     { label: "לא ידוע",  cls: "bg-gray-100 text-gray-600" },
};

const MT_RUN_STATUS_LABELS = {
  pending: "מאתחל", analyzing: "מנתח", diagnosing: "מאבחן",
  awaiting_technician_review: "ממתין לסקירה", awaiting_approval: "ממתין לאישור",
  waiting_for_customer_connection: "ממתין לחיבור", connection_active: "חיבור פעיל",
  waiting_for_customer_answer: "ממתין לתשובה", paused: "הופסק",
  approved: "מאושר", rejected: "נדחה", executing: "מבצע",
  resolved: "נפתרה", completed: "הושלם", failed: "נכשל",
};

export function mtRunStatusLabel(status) {
  if (status == null || status === "") return "—";
  return MT_RUN_STATUS_LABELS[status] ?? mtSafeText(status);
}

export const MT_DAYS_OPTIONS = [
  { value: 30, label: "30 ימים" },
  { value: 90, label: "90 ימים" },
  { value: 365, label: "שנה" },
  { value: 3650, label: "הכל" },
];

// ─── Assignment extraction ────────────────────────────────────────────────────
// The authoritative backend projection for this data is still in flight (sibling BACKEND-064);
// until it lands, these tolerant keys are accepted on the agent detail response. Explicit empty
// reported fallback = "none configured"; an absent/null key = "unknown", never invented.

const MT_ASSIGNMENT_KEYS = ["provider_model", "provider_model_assignment", "model_assignment"];
const MT_FALLBACK_KEYS = ["fallback", "fallback_chain", "fallbacks", "fallback_assignment"];

const mtScalar = (v) => (typeof v === "string" && v.trim() ? v : null);

function mtFallbackLabel(entry) {
  if (typeof entry === "string") return entry.trim() ? entry : null;
  if (entry && typeof entry === "object") {
    const provider = mtScalar(entry.provider);
    const model = mtScalar(entry.model);
    if (provider && model) return `${provider} · ${model}`;
    return model ?? provider;
  }
  return null;
}

export function mtExtractAssignment(detail) {
  let contract = null;
  if (detail && typeof detail === "object") {
    for (const key of MT_ASSIGNMENT_KEYS) {
      const value = detail[key];
      if (typeof value === "string" && value.trim()) { contract = { model: value }; break; }
      if (value && typeof value === "object" && !Array.isArray(value)) { contract = value; break; }
    }
  }
  if (!contract) {
    return { reported: false, model: null, provider: null, source: null, fallback: { status: "unknown", entries: [] } };
  }
  const fallbackKey = MT_FALLBACK_KEYS.find((key) => key in contract) ?? null;
  let fallback = { status: "unknown", entries: [] };
  if (fallbackKey != null && contract[fallbackKey] != null) {
    const raw = contract[fallbackKey];
    const list = Array.isArray(raw) ? raw : raw === "" ? [] : [raw];
    const entries = list.map(mtFallbackLabel).filter(Boolean);
    fallback = entries.length > 0 ? { status: "configured", entries } : { status: "none", entries: [] };
  }
  return {
    reported: true,
    model: mtScalar(contract.model ?? contract.model_name),
    provider: mtScalar(contract.provider ?? contract.provider_name),
    source: mtScalar(contract.source ?? contract.policy_source ?? contract.assignment_source),
    fallback,
  };
}

const MT_FALLBACK_UNKNOWN = "לא ידוע — מדיניות גיבוי טרם דווחה על-ידי השרת";
const MT_FALLBACK_NONE = "אין גיבוי מוגדר (דווח במפורש)";

// ─── Formatting ───────────────────────────────────────────────────────────────

const mtMoney = (v) => (typeof v === "number" && Number.isFinite(v) ? `$${v < 0.01 ? v.toFixed(4) : v.toFixed(2)}` : "—");
const mtMs = (v) => (typeof v === "number" && Number.isFinite(v) ? `${Math.round(v)}ms` : "—");
const mtInt = (v) => (typeof v === "number" && Number.isFinite(v) ? v.toLocaleString("he-IL") : "—");
const mtTokens = (r) => `${mtInt(r.input_tokens)}→${mtInt(r.output_tokens)}`;

function mtDate(iso) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("he-IL", { dateStyle: "short", timeStyle: "short" }).format(new Date(iso));
}

// ─── Small presentational pieces ──────────────────────────────────────────────

function HealthBadge({ state }) {
  const meta = MT_HEALTH_META[state] ?? { label: mtSafeText(state, "לא ידוע"), cls: "bg-gray-100 text-gray-600" };
  return <span className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${meta.cls}`}>{meta.label}</span>;
}

function ErrorBanner({ label, onRetry }) {
  return (
    <div role="alert" className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm flex items-center justify-between gap-3">
      <span>{label}</span>
      {onRetry && (
        <button type="button" onClick={onRetry} className="px-3 py-1 rounded-md border border-red-300 hover:bg-red-100 shrink-0">
          נסה שוב
        </button>
      )}
    </div>
  );
}

function LoadingLine({ label }) {
  return <p className="text-sm text-gray-500">{label}</p>;
}

function RollupRow({ summary }) {
  return (
    <tr className="border-t border-gray-100">
      <td className="py-2 px-2">{mtProviderLabel(summary.provider) ?? "לא ידוע"}</td>
      <td className="py-2 px-2 font-mono text-xs" dir="ltr">{mtSafeText(summary.model, "לא ידוע")}</td>
      <td className="py-2 px-2"><HealthBadge state={summary.health} /></td>
      <td className="py-2 px-2">{mtInt(summary.calls)}</td>
      <td className="py-2 px-2">{mtInt(summary.successes)}</td>
      <td className="py-2 px-2">{mtInt(summary.failures)}</td>
      <td className="py-2 px-2" title="קריאות שהופנו לספק/מודל אחר מזה שהתבקש">{mtInt(summary.fallback_count)}</td>
      <td className="py-2 px-2" title="קריאות שלא תועד עבורן מי שירת בפועל (לא ניתן לקבוע גיבוי)">{mtInt(summary.fallback_not_recorded_count)}</td>
      <td className="py-2 px-2">{mtMs(summary.latency_avg_ms)}</td>
      <td className="py-2 px-2" title={`קריאות מתומחרות: ${mtInt(summary.priced_calls)} · ללא תמחור: ${mtInt(summary.unpriced_calls)}`}>
        {mtMoney(summary.recorded_cost_total)}{summary.currency ? ` ${mtSafeText(summary.currency, "")}` : ""}
      </td>
      <td className="py-2 px-2 text-xs text-gray-500">
        {(summary.pricing_versions ?? []).map((v) => mtSafeText(v, "")).filter(Boolean).join(", ") || "—"}
      </td>
    </tr>
  );
}

function RollupTable({ summaries, ariaLabel }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-right" aria-label={ariaLabel}>
        <thead>
          <tr className="text-xs text-gray-500">
            <th scope="col" className="py-2 px-2 font-medium">ספק</th>
            <th scope="col" className="py-2 px-2 font-medium">מודל</th>
            <th scope="col" className="py-2 px-2 font-medium">בריאות</th>
            <th scope="col" className="py-2 px-2 font-medium">קריאות</th>
            <th scope="col" className="py-2 px-2 font-medium">הצלחות</th>
            <th scope="col" className="py-2 px-2 font-medium">כשלים</th>
            <th scope="col" className="py-2 px-2 font-medium">הופנו אחרת</th>
            <th scope="col" className="py-2 px-2 font-medium">ללא תיעוד שירות</th>
            <th scope="col" className="py-2 px-2 font-medium">תגובה ממוצעת</th>
            <th scope="col" className="py-2 px-2 font-medium">עלות שנרשמה</th>
            <th scope="col" className="py-2 px-2 font-medium">מחירון</th>
          </tr>
        </thead>
        <tbody>
          {summaries.map((s) => <RollupRow key={`${s.provider}|${s.model}`} summary={s} />)}
        </tbody>
      </table>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ModelProviderTransparencyPage() {
  const [days, setDays] = useState(90);
  const [selectedId, setSelectedId] = useState("");

  const { data: agents = [], isError: agentsFailed, isLoading: agentsLoading, refetch: refetchAgents } = useQuery({
    queryKey: ["mt-agents"],
    queryFn: agentsApi.list,
    staleTime: 60_000,
  });

  const activeId = agents.some((a) => a.id === selectedId) ? selectedId : (agents[0]?.id ?? "");
  const selectedAgent = agents.find((a) => a.id === activeId) ?? null;

  const { data: detail, isError: detailFailed, isLoading: detailLoading, refetch: refetchDetail } = useQuery({
    queryKey: ["mt-agent", activeId],
    queryFn: () => agentsApi.get(activeId),
    enabled: !!activeId,
  });

  const { data: runs = [], isError: runsFailed, isLoading: runsLoading, refetch: refetchRuns } = useQuery({
    queryKey: ["mt-runs", activeId, days],
    queryFn: () => agentRunsApi.listByAgent(activeId, { limit: 50, since_days: days }),
    enabled: !!activeId,
  });

  const { data: cost, isError: costFailed, refetch: refetchCost } = useQuery({
    queryKey: ["mt-cost", activeId, days],
    queryFn: () => agentRunsApi.costStats({ agent_id: activeId, since_days: days }),
    enabled: !!activeId,
  });

  const { data: health, isError: healthFailed, isLoading: healthLoading, refetch: refetchHealth } = useQuery({
    queryKey: ["mt-provider-health", days],
    queryFn: () => agentRunsApi.providerHealth({ since_days: days }),
  });

  const assignment = useMemo(() => mtExtractAssignment(detail), [detail]);
  const assignedModel = assignment.model ?? mtScalar(detail?.model) ?? null;
  const sourceText = assignment.source
    ? mtSourceLabel(assignment.source)
    : assignment.reported && assignment.model
    ? "לפי דיווח השרת"
    : "לפי רשומת העובד";

  const telemetryRuns = useMemo(
    () => runs.filter((r) => r.provider || r.model || r.estimated_cost != null),
    [runs],
  );

  const relevantSummaries = useMemo(() => {
    const summaries = health?.summaries ?? [];
    const model = (assignedModel ?? "").toLowerCase();
    const observed = new Set(
      telemetryRuns.map((r) => `${(r.provider ?? "").toLowerCase()}|${(r.model ?? "").toLowerCase()}`),
    );
    return summaries.filter((s) => {
      const pair = `${(s.provider ?? "").toLowerCase()}|${(s.model ?? "").toLowerCase()}`;
      if (observed.has(pair)) return true;
      return model && (s.model ?? "").toLowerCase() === model;
    });
  }, [health, telemetryRuns, assignedModel]);

  const caveatLines = useMemo(
    () => [...(health?.caveats ?? []), ...(health?.notes ?? [])].map((t) => mtSafeText(t, "")).filter(Boolean).slice(0, 12),
    [health],
  );

  return (
    <div className="p-4 lg:p-6 space-y-6 max-w-6xl" dir="rtl">
      <header className="space-y-2">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-gray-800">שקיפות מודל וספק</h1>
          <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">לקריאה בלבד</span>
        </div>
        <p className="text-sm text-gray-600 max-w-3xl">
          העמוד מסביר כיצד ה-AI של כל עובד דיגיטלי מנותב: איזה מודל הוקצה ומאין ההקצאה נלקחת, מה
          מדיניות הגיבוי (כפי שדווחה), ומה הראיות בפועל — איזה ספק ומודל שירתו ריצות אמת, מצב
          הבריאות והעלויות. מפתחות, טוקנים וסודות אינם מוצגים כאן.
        </p>
        <div className="flex items-center gap-2 text-sm">
          <label htmlFor="mt-days" className="text-gray-600">חלון זמן:</label>
          <select
            id="mt-days"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="border border-gray-300 rounded-md px-2 py-1 bg-white"
          >
            {MT_DAYS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </header>

      {agentsFailed && <ErrorBanner label="לא ניתן לטעון את רשימת העובדים הדיגיטליים." onRetry={refetchAgents} />}
      {!agentsFailed && agentsLoading && <LoadingLine label="טוען עובדים…" />}
      {!agentsFailed && !agentsLoading && agents.length === 0 && (
        <p className="text-sm text-gray-500">לא נמצאו עובדים דיגיטליים להצגה.</p>
      )}

      {selectedAgent && (
        <section className="space-y-4" aria-label="הסבר ניתוב לעובד נבחר">
          <div className="flex items-center gap-3 flex-wrap">
            <label htmlFor="mt-employee" className="text-sm text-gray-700">עובד דיגיטלי:</label>
            <select
              id="mt-employee"
              value={activeId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-1 bg-white min-w-[16rem]"
            >
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {mtSafeText(a.name, "עובד")}{a.role_title ? ` · ${mtSafeText(a.role_title, "")}` : ""}{a.is_enabled ? "" : " (מושבת)"}
                </option>
              ))}
            </select>
            {!selectedAgent.is_enabled && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">העובד מושבת — הנתונים מוצגים לקריאה בלבד</span>
            )}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
              <h2 className="font-semibold text-gray-800">הקצאת מודל וספק</h2>
              {detailFailed && <ErrorBanner label="לא ניתן לטעון את הגדרת המודל של העובד." onRetry={refetchDetail} />}
              {!detailFailed && detailLoading && <LoadingLine label="טוען הקצאה…" />}
              {!detailFailed && !detailLoading && (
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">מודל מוקצה</dt>
                    <dd className="font-mono text-xs" dir="ltr">{mtSafeText(assignedModel, "לא ידוע")}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">מקור ההקצאה</dt>
                    <dd>{sourceText}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">ספק</dt>
                    <dd>
                      {assignment.provider
                        ? (mtProviderLabel(assignment.provider) ?? "לא ידוע")
                        : "לא מתועד ברשומת העובד"}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500 shrink-0">מדיניות גיבוי</dt>
                    <dd className="text-left">
                      {assignment.fallback.status === "configured" && (
                        <span className="flex flex-wrap gap-1 justify-end">
                          {assignment.fallback.entries.map((entry, i) => (
                            <span key={i} className="px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 font-mono" dir="ltr">
                              {mtSafeText(entry)}
                            </span>
                          ))}
                        </span>
                      )}
                      {assignment.fallback.status === "none" && MT_FALLBACK_NONE}
                      {assignment.fallback.status === "unknown" && MT_FALLBACK_UNKNOWN}
                    </dd>
                  </div>
                </dl>
              )}
              <p className="text-xs text-gray-400 border-t border-gray-100 pt-2">
                לקריאה בלבד — אין כאן שינוי מדיניות ניתוב, הרשאות או קצב עלות.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
              <h2 className="font-semibold text-gray-800">ראיות ניתוב בפועל</h2>
              {runsFailed && <ErrorBanner label="לא ניתן לטעון את ראיות הניתוב של העובד." onRetry={refetchRuns} />}
              {!runsFailed && runsLoading && <LoadingLine label="טוען ראיות…" />}
              {!runsFailed && !runsLoading && telemetryRuns.length === 0 && (
                <p className="text-sm text-gray-500">טרם נרשמו ריצות עם תיעוד ספק/מודל עבור עובד זה.</p>
              )}
              {!runsFailed && !runsLoading && telemetryRuns.length > 0 && (
                <>
                  <p className="text-sm text-gray-600">
                    הריצה האחרונה עם תיעוד: {mtDate(telemetryRuns[0].created_at)} ·{" "}
                    {mtProviderLabel(telemetryRuns[0].provider) ?? "ספק לא מתועד"} ·{" "}
                    <span className="font-mono text-xs" dir="ltr">{mtSafeText(telemetryRuns[0].model, "מודל לא מתועד")}</span>
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-right" aria-label="ריצות אחרונות עם תיעוד ספק ומודל">
                      <thead>
                        <tr className="text-xs text-gray-500">
                          <th scope="col" className="py-1 px-2 font-medium">זמן</th>
                          <th scope="col" className="py-1 px-2 font-medium">סטטוס</th>
                          <th scope="col" className="py-1 px-2 font-medium">ספק</th>
                          <th scope="col" className="py-1 px-2 font-medium">מודל</th>
                          <th scope="col" className="py-1 px-2 font-medium">עלות</th>
                          <th scope="col" className="py-1 px-2 font-medium">טוקנים</th>
                          <th scope="col" className="py-1 px-2 font-medium">ריצה</th>
                        </tr>
                      </thead>
                      <tbody>
                        {telemetryRuns.slice(0, 8).map((r) => (
                          <tr key={r.id} className="border-t border-gray-100">
                            <td className="py-1 px-2 whitespace-nowrap">{mtDate(r.created_at)}</td>
                            <td className="py-1 px-2 whitespace-nowrap">{mtRunStatusLabel(r.status)}</td>
                            <td className="py-1 px-2">{mtProviderLabel(r.provider) ?? "לא תועד"}</td>
                            <td className="py-1 px-2 font-mono text-xs" dir="ltr">{mtSafeText(r.model, "לא תועד")}</td>
                            <td className="py-1 px-2">{mtMoney(r.estimated_cost)}</td>
                            <td className="py-1 px-2 whitespace-nowrap">{mtTokens(r)}</td>
                            <td className="py-1 px-2">
                              <Link to={`/agent-runs/${r.id}`} className="text-brand-600 hover:underline">לצפייה בריצה</Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              <p className="text-xs text-gray-400 border-t border-gray-100 pt-2">
                המקור: טלמטריית ריצות (ספק, מודל, עלות) כפי שנרשמה בזמן אמת. ריצות ללא תיעוד אינן מוצגות.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
              <h2 className="font-semibold text-gray-800">עלות — לפי ספק ומודל</h2>
              {costFailed && <ErrorBanner label="לא ניתן לטעון את נתוני העלות של העובד." onRetry={refetchCost} />}
              {!costFailed && cost && (
                <>
                  <div className="flex flex-wrap gap-3"><RunCostChips cost={cost} /></div>
                  {(cost.by_model ?? []).length === 0 ? (
                    <p className="text-sm text-gray-500">אין ריצות בחלון הזמן שנבחר.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-right" aria-label="עלות לפי ספק ומודל">
                        <thead>
                          <tr className="text-xs text-gray-500">
                            <th scope="col" className="py-1 px-2 font-medium">ספק</th>
                            <th scope="col" className="py-1 px-2 font-medium">מודל</th>
                            <th scope="col" className="py-1 px-2 font-medium">ריצות</th>
                            <th scope="col" className="py-1 px-2 font-medium">טוקני קלט</th>
                            <th scope="col" className="py-1 px-2 font-medium">טוקני פלט</th>
                            <th scope="col" className="py-1 px-2 font-medium">עלות</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cost.by_model.map((m, i) => (
                            <tr key={`${m.provider}|${m.model}|${i}`} className="border-t border-gray-100">
                              <td className="py-1 px-2">{mtProviderLabel(m.provider) ?? "לא ידוע"}</td>
                              <td className="py-1 px-2 font-mono text-xs" dir="ltr">{mtSafeText(m.model, "לא ידוע")}</td>
                              <td className="py-1 px-2">{mtInt(m.runs)}</td>
                              <td className="py-1 px-2">{mtInt(m.input_tokens)}</td>
                              <td className="py-1 px-2">{mtInt(m.output_tokens)}</td>
                              <td className="py-1 px-2">{mtMoney(m.cost_usd)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <p className="text-xs text-gray-400">
                    העלות מחושבת רק עבור ריצות שיש להן תמחור מוכר; ריצות ללא תמחור אינן מיוצגות בסכום.
                  </p>
                </>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
              <h2 className="font-semibold text-gray-800">בריאות הספק והמודל של העובד</h2>
              {healthFailed && <ErrorBanner label="לא ניתן לטעון את מצב הספקים והמודלים." onRetry={refetchHealth} />}
              {!healthFailed && healthLoading && <LoadingLine label="טוען מצב בריאות…" />}
              {!healthFailed && !healthLoading && relevantSummaries.length === 0 && (
                <p className="text-sm text-gray-500">לא נמצא תיעוד תפעולי לספק/מודל של עובד זה בחלון הזמן שנבחר.</p>
              )}
              {!healthFailed && !healthLoading && relevantSummaries.length > 0 && (
                <RollupTable summaries={relevantSummaries} ariaLabel="בריאות הספק והמודל של העובד" />
              )}
            </div>
          </div>
        </section>
      )}

      <section className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" aria-label="מצב כל הספקים והמודלים">
        <h2 className="font-semibold text-gray-800">מצב כל הספקים והמודלים שנראו בתעבורה</h2>
        {healthFailed && <ErrorBanner label="לא ניתן לטעון את מצב הספקים והמודלים." onRetry={refetchHealth} />}
        {!healthFailed && healthLoading && <LoadingLine label="טוען מצב ספקים…" />}
        {!healthFailed && !healthLoading && health && (
          <>
            <p className="text-sm text-gray-600">
              סה״כ {mtInt(health.total_calls)} קריאות ספק נרשמו בחלון שנבחר ·{" "}
              {health.total_fallbacks > 0
                ? `${mtInt(health.total_fallbacks)} הופנו לספק/מודל אחר`
                : "לא נרשמו הפניות לספק/מודל אחר"}
              .
            </p>
            {(health.summaries ?? []).length === 0 ? (
              <p className="text-sm text-gray-500">לא נרשמו קריאות ספק בחלון הזמן שנבחר — אין עדיין ראיות תפעוליות להצגה.</p>
            ) : (
              <RollupTable summaries={health.summaries} ariaLabel="מצב כל הספקים והמודלים" />
            )}
            {caveatLines.length > 0 && (
              <details className="text-xs text-gray-500">
                <summary className="cursor-pointer">מגבלות הנתונים</summary>
                <ul className="list-disc pr-5 mt-1 space-y-0.5">
                  {caveatLines.map((line, i) => <li key={i}>{line}</li>)}
                </ul>
              </details>
            )}
          </>
        )}
      </section>
    </div>
  );
}
